import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { WithContext, Thing, Course } from 'schema-dts';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  jsonLd?: WithContext<Thing> | WithContext<Thing>[];
  noindex?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

interface CourseSchemaProps {
  name: string;
  description: string;
  provider: string;
  url: string;
  image: string;
  price: number;
  duration: string;
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  publisher: string;
}

export function generateCourseSchema(props: CourseSchemaProps): WithContext<Course> {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": props.name,
    "description": props.description,
    "provider": {
      "@type": "Organization",
      "name": props.provider,
      "sameAs": "https://one80labs.com"
    },
    "url": props.url,
    "image": props.image,
    "offers": {
      "@type": "Offer",
      "price": props.price,
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString()
    },
    "timeRequired": props.duration,
    "educationalLevel": "Beginner to Intermediate",
    "teaches": [
      "AI implementation",
      "Workflow automation",
      "Business productivity",
      "Technology optimization"
    ],
    "provider": {
      "@type": "Organization",
      "name": "One80Labs",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4383 Nicholas Street #302",
        "addressLocality": "Omaha",
        "addressRegion": "NE",
        "postalCode": "68181",
        "addressCountry": "US"
      }
    }
  };
}

export function generateArticleSchema(props: ArticleSchemaProps): WithContext<Thing> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": props.headline,
    "description": props.description,
    "image": props.image,
    "datePublished": props.datePublished,
    "dateModified": props.dateModified,
    "author": {
      "@type": "Person",
      "name": props.author
    },
    "publisher": {
      "@type": "Organization",
      "name": props.publisher,
      "logo": {
        "@type": "ImageObject",
        "url": "https://one80labs.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://one80labs.com"
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["article", ".blog-title", ".blog-meta"]
    },
    "isAccessibleForFree": "True"
  };
}

export default function SEO({
  title = 'One80Labs - AI Training & Fractional Project Management in Omaha, Nebraska',
  description = 'Transform your business with AI-powered project management and training in Omaha and the Midwest. Expert services in ChatGPT, Gemini, and Microsoft 365 Copilot.',
  image = 'https://images.unsplash.com/photo-1677442135968-6d89485dd1d1',
  article = false,
  jsonLd,
  noindex = false,
  keywords = [],
  author,
  publishedTime,
  modifiedTime,
  section,
  tags = []
}: SEOProps) {
  const { pathname } = useLocation();
  const siteUrl = 'https://one80labs.com';
  const canonical = `${siteUrl}${pathname}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content="One80Labs" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      {article && author && <meta property="article:author" content={author} />}
      {article && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {article && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {article && section && <meta property="article:section" content={section} />}
      {article && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@one80labs" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {author && <meta name="twitter:creator" content={author} />}

      {/* JSON-LD */}
      {Array.isArray(jsonLd) ? (
        jsonLd.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))
      ) : (
        jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        )
      )}
      
      {/* Geo Location for Local SEO */}
      <meta name="geo.region" content="US-NE" />
      <meta name="geo.placename" content="Omaha" />
      <meta name="geo.position" content="41.2565;-95.9345" />
      <meta name="ICBM" content="41.2565, -95.9345" />
      
      {/* Voice Search Optimization */}
      <meta name="apple-itunes-app" content="app-id=123456789" />
      <meta name="google-site-verification" content="verification_token" />
      
      {/* Accessibility */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      
      {/* Additional meta tags for voice search and AEO */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="apple-mobile-web-app-title" content="One80Labs" />
      
      {/* Speakable markup for voice search */}
      <meta name="speakable" content="speakable" />
    </Helmet>
  );
}